import { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  TextField,
  useMediaQuery,
  Stack,
  CircularProgress,
  Alert
} from '@mui/material';
import { CameraAlt, Upload } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { inputSetScore, saveScorecardPhoto } from '../../features/game/gameSlice';
import { useTheme } from '@mui/material/styles';
import { prepareScorecardPhoto } from '../../helpers/imageResize';

const ScoreInput = ({
  teamName,
  control,
  name,
  isMobile,
  showLabel = true,
  compact = false,
  maxValue = 999,
  allowDecimal = false,
  onValueChange
}) => {
  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      alignContent={'space-between'}
      textAlign={'center'}>
      {showLabel && (
        <Typography variant={isMobile ? 'body2' : 'body1'} fontWeight="bold" sx={{ mb: 1 }}>
          {teamName}
        </Typography>
      )}
      <Controller
        name={name}
        control={control}
        defaultValue=""
        render={({ field }) => (
          <TextField
            {...field}
            value={field.value ?? ''}
            variant="outlined"
            type={allowDecimal ? 'text' : 'number'}
            inputProps={
              allowDecimal
                ? {
                    // As in inputScoreDialog: iOS drops inputmode="decimal"
                    // and needs the pattern to raise a pad with a decimal key.
                    inputMode: 'decimal',
                    pattern: '[0-9.]*',
                    min: 0,
                    max: maxValue
                  }
                : { min: 0, max: maxValue }
            }
            autoComplete="off"
            onChange={(event) => {
              // Comma-decimal iPhone locales send ',' from the decimal key.
              const nextValue = event.target.value.replace(/,/g, '.');
              const valid = allowDecimal ? /^\d*\.?\d*$/.test(nextValue) : /^\d+$/.test(nextValue);

              if (nextValue === '' || valid) {
                if (onValueChange) {
                  onValueChange(nextValue, field);
                } else {
                  field.onChange(nextValue);
                }
              }
            }}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-input': {
                textAlign: 'center',
                fontSize: compact
                  ? isMobile
                    ? '1.35rem'
                    : '1.9rem'
                  : isMobile
                    ? '1.75rem'
                    : '2.5rem',
                padding: compact ? (isMobile ? '8px' : '10px') : isMobile ? '10px' : '14px',
                fontWeight: 'bold'
              }
            }}
            placeholder="0"
            fullWidth
          />
        )}
      />
    </Box>
  );
};

const InputTotalsDialog = ({
  game,
  onOpen,
  submitScore,
  savePhoto = null,
  submitting = false,
  allowPhotos = true,
  triggerColor = 'secondary',
  triggerSx = null
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const inputtingSetScore = useSelector((state) => state.game.inputtingSetScore);
  const busy = submitting || inputtingSetScore;

  const [open, setOpen] = useState(false);
  const [scorecardPhoto, setScorecardPhoto] = useState(null);
  const [savingPhoto, setSavingPhoto] = useState(false);
  const [processingPhoto, setProcessingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState(null);

  const cameraInputRef = useRef(null);
  const uploadInputRef = useRef(null);
  const processingPhotoRef = useRef(false);

  const teamA = game.teams.teamA;
  const teamB = game.teams.teamB;

  // Total game points cap: win points + set points (unless excluded) +
  // per-end bonus across all ends and skins/sets. Ends 0 (unlimited) falls
  // back to 1 per skin/set, mirroring the extra-end cap in inputScoreDialog.
  const endsPerSet = Number(game.gameFormat?.ends || 0);
  const sets = Math.max(Number(game.gameFormat?.sets || 0), 1);
  const bonusPool =
    Number(game.gameFormat?.extraEndGamePoint || 0) * (endsPerSet > 0 ? endsPerSet : 1) * sets;
  const setPointsPool = game.gameFormat?.excludeSetPointInGamePoints
    ? 0
    : Number(game.gameFormat?.maxTotalPerSet || 0) * sets;
  const winPoints = Number(game.gameFormat?.winPoints || 0);
  const gamePointsCap = winPoints + setPointsPool + bonusPool;
  const sumToCapEnabled = gamePointsCap > 0;

  const { control, setValue, watch, reset, handleSubmit: rhfHandleSubmit } = useForm({
    mode: 'onChange',
    defaultValues: {
      shotsA: '',
      shotsB: '',
      pointsA: '',
      pointsB: '',
      tiebreakerA: '',
      tiebreakerB: ''
    }
  });

  const allValues = watch();

  const shotsA = parseInt(allValues.shotsA, 10) || 0;
  const shotsB = parseInt(allValues.shotsB, 10) || 0;
  const pointsA = parseFloat(allValues.pointsA || 0) || 0;
  const pointsB = parseFloat(allValues.pointsB || 0) || 0;

  const shotsEntered = allValues.shotsA !== '' && allValues.shotsB !== '';
  const shotsEqual = shotsEntered && shotsA === shotsB;
  const needsTiebreaker = Boolean(game.gameFormat?.playoffs) && shotsEqual;

  const handleGamePointsChange = (team, value) => {
    const sourceName = team === 'A' ? 'pointsA' : 'pointsB';
    const targetName = team === 'A' ? 'pointsB' : 'pointsA';

    if (!sumToCapEnabled) {
      setValue(sourceName, value, { shouldDirty: true, shouldValidate: true });
      return;
    }

    if (value === '') {
      setValue(sourceName, '', { shouldDirty: true, shouldValidate: true });
      setValue(targetName, '', { shouldDirty: true, shouldValidate: true });
      return;
    }

    if (/\.$/.test(value) || /\.\d*0$/.test(value)) {
      setValue(sourceName, value, { shouldDirty: true, shouldValidate: false });
      return;
    }

    const numericValue = parseFloat(value);
    if (!Number.isFinite(numericValue)) {
      return;
    }

    const boundedValue = Math.max(0, Math.min(numericValue, gamePointsCap));
    const remaining = Math.max(gamePointsCap - boundedValue, 0);

    setValue(sourceName, `${boundedValue}`, { shouldDirty: true, shouldValidate: true });
    setValue(targetName, `${remaining}`, { shouldDirty: true, shouldValidate: true });
  };

  const handleClickOpen = () => {
    setOpen(true);
    setScorecardPhoto(null);
    setPhotoError(null);
    reset();
  };

  const handleClose = () => {
    setOpen(false);
    reset();
    setScorecardPhoto(null);
    setPhotoError(null);
  };

  const validateForm = () => {
    if (!shotsEntered) {
      alert('Please enter the total shots for both teams');
      return false;
    }

    if (sumToCapEnabled && (allValues.pointsA === '' || allValues.pointsB === '')) {
      alert('Please enter the total game points for both teams');
      return false;
    }

    // Compare rounded to 2dp so auto-filled values like 10.7 + 1.3000000000000007
    // don't fail the cap check on floating point error.
    if (
      sumToCapEnabled &&
      Math.round(pointsA * 100) + Math.round(pointsB * 100) !== Math.round(gamePointsCap * 100)
    ) {
      alert(`The total game points must add up to ${gamePointsCap}`);
      return false;
    }

    if (needsTiebreaker) {
      const tiebreakerA = allValues.tiebreakerA;
      const tiebreakerB = allValues.tiebreakerB;
      if (!tiebreakerA || tiebreakerA === '' || !tiebreakerB || tiebreakerB === '') {
        alert('Please enter both tiebreaker scores');
        return false;
      }
    }

    return true;
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    event.target.value = '';
    setPhotoError(null);

    if (processingPhotoRef.current) return;
    if (!file) {
      setPhotoError('No file selected');
      return;
    }

    processingPhotoRef.current = true;
    setProcessingPhoto(true);

    prepareScorecardPhoto(file, { isMobile })
      .then((resizedImage) => {
        setScorecardPhoto(resizedImage);
        setPhotoError(null);
      })
      .catch((error) => {
        setPhotoError(error?.message || 'Failed to process image. Please try again.');
      })
      .finally(() => {
        processingPhotoRef.current = false;
        setProcessingPhoto(false);
      });
  };

  const handleTakePhoto = () => {
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
      cameraInputRef.current.click();
    }
  };

  const handleUploadPhoto = () => {
    if (uploadInputRef.current) {
      uploadInputRef.current.value = '';
      uploadInputRef.current.click();
    }
  };

  const handleClearPhoto = () => {
    setScorecardPhoto(null);
    setPhotoError(null);
  };

  const onSubmit = async (data) => {
    if (processingPhoto) return;
    if (!validateForm()) return;

    const submitData = {
      game,
      gameId: game.id,
      totalsOnly: true,
      scores: [
        {
          set: 1,
          teams: {
            [teamA.id]: shotsA,
            [teamB.id]: shotsB
          }
        }
      ],
      totalPoints: {
        [teamA.id]: pointsA,
        [teamB.id]: pointsB
      },
      teamExtraGamePoints: {
        [teamA.id]: 0,
        [teamB.id]: 0
      }
    };

    if (needsTiebreaker) {
      submitData.tiebreakerScore = {
        [teamA.id]: parseInt(data.tiebreakerA, 10),
        [teamB.id]: parseInt(data.tiebreakerB, 10)
      };
    }

    setSavingPhoto(true);
    try {
      if (allowPhotos && scorecardPhoto) {
        if (savePhoto) {
          await savePhoto(scorecardPhoto);
        } else {
          await dispatch(
            saveScorecardPhoto({ gameId: game.id, image: scorecardPhoto })
          ).unwrap();
        }
      }
    } catch (error) {
      // Continue score submission even if photo upload fails.
      // eslint-disable-next-line no-console
      console.error('Error saving photo:', error);
    } finally {
      setSavingPhoto(false);
    }

    try {
      if (submitScore) {
        await submitScore(submitData);
      } else {
        await dispatch(inputSetScore(submitData)).unwrap();
      }

      handleClose();
      if (onOpen) {
        onOpen();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error submitting scores:', error);
      alert(
        error?.response?.data?.message ||
          error?.message ||
          'Error submitting scores. Please try again.'
      );
    }
  };

  const renderSummaryRow = (leftValue, label, rightValue, valueColor = 'success.main') => (
    <Box display={'grid'} gridTemplateColumns={'1fr 1.6fr 1fr'} alignItems={'center'} columnGap={2}>
      <Typography
        variant={isMobile ? 'body1' : 'h6'}
        color={valueColor}
        fontWeight="bold"
        textAlign="center">
        {leftValue}
      </Typography>
      <Typography variant={isMobile ? 'body2' : 'body1'} fontWeight="bold" textAlign="center">
        {label}
      </Typography>
      <Typography
        variant={isMobile ? 'body1' : 'h6'}
        color={valueColor}
        fontWeight="bold"
        textAlign="center">
        {rightValue}
      </Typography>
    </Box>
  );

  return (
    <>
      <Button
        variant="contained"
        color={triggerColor}
        onClick={handleClickOpen}
        fullWidth={isMobile}
        sx={triggerSx}>
        Input Score
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 2 } }}>
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Box>
            <Typography variant={isMobile ? 'h6' : 'h5'} component="div">
              Score Game
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {game.name ?? 'Private Game'} - Totals Only
            </Typography>
          </Box>
        </DialogTitle>

        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <input
          ref={uploadInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        <Box
          component="form"
          onSubmit={rhfHandleSubmit(onSubmit)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: '1 1 auto',
            minHeight: 0
          }}>
          <DialogContent
            sx={{
              px: isMobile ? 1.5 : 3,
              pt: isMobile ? 2 : 4,
              pb: isMobile ? 3 : 4,
              overflowY: 'auto'
            }}>
            <Stack spacing={isMobile ? 2 : 3}>
              <Box
                sx={{
                  p: isMobile ? 1.5 : 2,
                  border: '2px solid',
                  borderColor: 'primary.main',
                  borderRadius: 1
                }}>
                <Typography
                  variant={isMobile ? 'body2' : 'body1'}
                  fontWeight="bold"
                  sx={{ mb: isMobile ? 1 : 1.5, color: 'primary.main', textAlign: 'center' }}>
                  Total Shots
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: isMobile ? 1 : 2
                  }}>
                  <ScoreInput control={control} name="shotsA" teamName={teamA.name} isMobile={isMobile} />
                  <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
                    <Typography variant={isMobile ? 'body2' : 'body1'} fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
                      VS
                    </Typography>
                  </Box>
                  <ScoreInput control={control} name="shotsB" teamName={teamB.name} isMobile={isMobile} />
                </Box>
              </Box>

              <Box
                sx={{
                  p: isMobile ? 1.5 : 2,
                  border: '2px solid',
                  borderColor: 'success.main',
                  borderRadius: 1
                }}>
                <Typography
                  variant={isMobile ? 'body2' : 'body1'}
                  fontWeight="bold"
                  sx={{ mb: 1, color: 'success.main' }}>
                  Total Game Points
                </Typography>
                {sumToCapEnabled ? (
                  <Typography
                    variant="caption"
                    color="success.main"
                    sx={{ mb: isMobile ? 1.5 : 2, display: 'block' }}>
                    Auto-fill enabled: total between both teams must equal {gamePointsCap} (win
                    points + set points + bonus points).
                  </Typography>
                ) : null}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: isMobile ? 1 : 2
                  }}>
                  <ScoreInput
                    control={control}
                    name="pointsA"
                    teamName={teamA.name}
                    isMobile={isMobile}
                    compact
                    allowDecimal
                    maxValue={sumToCapEnabled ? gamePointsCap : 999}
                    onValueChange={(value) => handleGamePointsChange('A', value)}
                  />
                  <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
                    <Typography variant={isMobile ? 'body2' : 'body1'} fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
                      VS
                    </Typography>
                  </Box>
                  <ScoreInput
                    control={control}
                    name="pointsB"
                    teamName={teamB.name}
                    isMobile={isMobile}
                    compact
                    allowDecimal
                    maxValue={sumToCapEnabled ? gamePointsCap : 999}
                    onValueChange={(value) => handleGamePointsChange('B', value)}
                  />
                </Box>
              </Box>

              {needsTiebreaker && (
                <Box
                  sx={{
                    p: isMobile ? 1.5 : 2,
                    border: '2px solid',
                    borderColor: 'warning.main',
                    borderRadius: 1
                  }}>
                  <Typography
                    variant={isMobile ? 'body2' : 'body1'}
                    fontWeight="bold"
                    sx={{ mb: 1, color: 'warning.dark' }}>
                    ⚠ Tiebreaker
                  </Typography>
                  <Typography
                    variant="caption"
                    color="warning.dark"
                    sx={{ mb: isMobile ? 1.5 : 2, display: 'block' }}>
                    Total shots are tied - Enter tiebreaker score
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: isMobile ? 1 : 2
                    }}>
                    <ScoreInput
                      control={control}
                      name="tiebreakerA"
                      teamName={teamA.name}
                      isMobile={isMobile}
                    />
                    <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
                      <Typography variant={isMobile ? 'body2' : 'body1'} fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
                        VS
                      </Typography>
                    </Box>
                    <ScoreInput
                      control={control}
                      name="tiebreakerB"
                      teamName={teamB.name}
                      isMobile={isMobile}
                    />
                  </Box>
                </Box>
              )}

              <Box
                sx={{
                  p: isMobile ? 1.5 : 2,
                  border: '2px solid',
                  borderColor: 'success.main',
                  borderRadius: 1
                }}>
                <Typography
                  variant={isMobile ? 'body2' : 'body1'}
                  fontWeight="bold"
                  sx={{ mb: 1.5, color: 'success.main', textAlign: 'center' }}>
                  Summary
                </Typography>

                <Stack spacing={1.25}>
                  {renderSummaryRow(shotsA, 'Total Shots', shotsB, 'primary.main')}
                  {shotsEntered && !shotsEqual && (
                    <Typography variant="caption" color="text.secondary" textAlign="center">
                      {shotsA > shotsB ? teamA.name : teamB.name} leads by{' '}
                      {Math.abs(shotsA - shotsB)} shots
                      {winPoints > 0 ? ` - ${winPoints} win points` : ''}
                    </Typography>
                  )}
                  {shotsEqual && shotsEntered && (
                    <Typography variant="caption" color="text.secondary" textAlign="center">
                      Shots are tied
                    </Typography>
                  )}
                  {bonusPool > 0 && (
                    <Typography variant="caption" color="info.main" textAlign="center">
                      Of which {bonusPool} are bonus points
                    </Typography>
                  )}
                  {renderSummaryRow(pointsA, 'Total Game Points', pointsB)}
                </Stack>
              </Box>

              {allowPhotos && (
              <Box
                sx={{
                  p: isMobile ? 1.5 : 2,
                  border: '2px solid',
                  borderColor: 'info.main',
                  borderRadius: 1
                }}>
                <Typography
                  variant={isMobile ? 'body2' : 'body1'}
                  fontWeight="bold"
                  sx={{ mb: 1, color: 'info.dark' }}>
                  📸 Scoreboard Photo
                </Typography>

                {photoError && (
                  <Alert severity="error" sx={{ mb: 1.5 }}>
                    {photoError}
                  </Alert>
                )}

                {processingPhoto && (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">
                      Preparing photo...
                    </Typography>
                  </Stack>
                )}

                {scorecardPhoto ? (
                  <Box>
                    <Box
                      component="img"
                      src={scorecardPhoto}
                      alt="Scoreboard"
                      sx={{
                        maxWidth: '100%',
                        maxHeight: isMobile ? 150 : 200,
                        borderRadius: 1,
                        border: '2px solid',
                        borderColor: 'info.main',
                        mb: 1.5
                      }}
                    />
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleClearPhoto}
                        disabled={processingPhoto}
                        fullWidth
                        size={isMobile ? 'small' : 'medium'}>
                        Remove
                      </Button>
                      <Button
                        variant="outlined"
                        color="info"
                        onClick={handleUploadPhoto}
                        disabled={processingPhoto}
                        fullWidth
                        size={isMobile ? 'small' : 'medium'}>
                        Change
                      </Button>
                    </Stack>
                  </Box>
                ) : (
                  <Stack spacing={1}>
                    <Button
                      variant="contained"
                      color="info"
                      startIcon={<CameraAlt />}
                      onClick={handleTakePhoto}
                      disabled={processingPhoto}
                      fullWidth
                      size={isMobile ? 'small' : 'medium'}>
                      Take Photo
                    </Button>
                    <Button
                      variant="outlined"
                      color="info"
                      startIcon={<Upload />}
                      onClick={handleUploadPhoto}
                      disabled={processingPhoto}
                      fullWidth
                      size={isMobile ? 'small' : 'medium'}>
                      Upload Photo
                    </Button>
                  </Stack>
                )}
              </Box>
              )}
            </Stack>
          </DialogContent>

          <DialogActions
            sx={{
              px: isMobile ? 1 : 2,
              pt: isMobile ? 1 : 2,
              pb: isMobile ? 'max(8px, env(safe-area-inset-bottom))' : 2,
              flexDirection: isMobile ? 'column-reverse' : 'row',
              gap: isMobile ? 1 : 0,
              borderTop: 1,
              borderColor: 'divider',
              backgroundColor: 'background.paper'
            }}>
            <Button
              onClick={handleClose}
              color="primary"
              fullWidth={isMobile}
              size={isMobile ? 'small' : 'medium'}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="success"
              disabled={savingPhoto || busy || processingPhoto}
              fullWidth={isMobile}
              size={isMobile ? 'medium' : 'large'}
              sx={{ minHeight: 48, fontWeight: 700 }}>
              {busy ? <CircularProgress size={20} color="inherit" /> : 'Submit'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
};

export default InputTotalsDialog;
